import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { SITE, IMAGES } from "@/data/site";

const EASE = [0.76, 0, 0.24, 1];

const MaskLine = ({ children, delay = 0, className = "" }) => (
    <span className={`mask-line ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.15, ease: EASE, delay }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            style={{ position: "relative" }}
            className="min-h-[100svh] w-full overflow-hidden bg-[var(--ink)]"
        >
            {/* Background image with parallax */}
            <motion.div
                style={{ y, scale }}
                className="absolute inset-0 -z-10"
            >
                <img
                    src={IMAGES.hero}
                    alt="Crianças jogando futebol ao pôr do sol"
                    className="hero-media h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[var(--ink)]/70" />
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)]/40 via-transparent to-[var(--ink)]" />
            </motion.div>

            {/* Editorial grid lines */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="container-x h-full relative">
                    <div className="absolute inset-y-0 left-6 md:left-12 w-px bg-white/10" />
                    <div className="absolute inset-y-0 right-6 md:right-12 w-px bg-white/10" />
                </div>
            </div>

            {/* Top meta strip */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.8, ease: EASE }}
                className="container-x pt-28 md:pt-32"
            >
                <div className="flex items-center justify-between text-white/60 font-body text-[11px] tracking-[0.32em] uppercase">
                    <span data-testid="hero-meta-left">
                        ★ OSC · Desde {SITE.since}
                    </span>
                    <span className="hidden sm:inline">
                        Canaã dos Carajás — PA
                    </span>
                    <span data-testid="hero-meta-right">N°01 / 2025</span>
                </div>
            </motion.div>

            {/* Main headline */}
            <motion.div
                style={{ opacity }}
                className="container-x relative z-10 pt-10 md:pt-14"
            >
                <h1
                    data-testid="hero-headline"
                    className="font-display text-white text-[16vw] md:text-[13vw] lg:text-[11.5vw]"
                >
                    <MaskLine delay={0.05}>Ir além</MaskLine>
                    <MaskLine delay={0.18} className="text-[var(--sun)] italic">
                        <span className="font-editorial not-italic">do</span>{" "}
                        <span className="font-editorial italic">futebol.</span>
                    </MaskLine>
                    <MaskLine delay={0.32}>Formando</MaskLine>
                    <MaskLine delay={0.44}>cidadãos.</MaskLine>
                </h1>
            </motion.div>

            {/* Sub grid */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
                className="container-x mt-14 md:mt-24 grid grid-cols-12 gap-6 pb-24"
            >
                <div className="col-span-12 md:col-span-5 md:col-start-1">
                    <p className="font-body text-white/70 text-[14px] md:text-[15px] leading-relaxed max-w-md">
                        Associação Esportiva Novo Horizonte, em Canaã dos
                        Carajás — Pará. Desde 2005, transformando vidas por meio
                        do esporte, educação e cidadania. Atualmente com o{" "}
                        <span className="text-[var(--sun)]">
                            Projeto Meninas Vencedoras
                        </span>{" "}
                        (2025—2026) — patrocínio Eletrobras, apoio Ministério
                        do Esporte.
                    </p>
                </div>

                <div className="col-span-12 md:col-span-4 md:col-start-8 flex md:justify-end items-end gap-4">
                    <button
                        data-testid="hero-cta-primary"
                        onClick={() =>
                            document
                                .getElementById("historia")
                                ?.scrollIntoView({ behavior: "smooth" })
                        }
                        className="btn-brutal"
                    >
                        Nossa História
                        <ArrowDown size={16} strokeWidth={2.5} />
                    </button>
                </div>

                <div className="col-span-12 mt-10 flex items-end justify-between text-white/50 font-body text-[10px] tracking-[0.32em] uppercase">
                    <div className="flex items-center gap-3">
                        <span className="inline-block h-px w-8 bg-white/40" />
                        <span data-testid="hero-scroll-hint">
                            Role para começar
                        </span>
                    </div>
                    <span className="hidden md:inline">
                        {SITE.cnpj}
                    </span>
                </div>
            </motion.div>
        </section>
    );
};
